/* =========================================================
   KITCHENBUILDR HEADER v2
   1. Search: the icon opens a panel under the header (field, live results,
      popular searches and categories). "/" or Ctrl/Cmd+K opens it.
   2. Cart: keeps the Kitchen Cart button (count, total, free delivery
      ring) in step with any add to cart on the page, with a small bump.
   3. Menu: underlines the item for the page (and # section) you are on.
========================================================= */
(() => {
  if (window.KBHeader) return;

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* ------------------------------------------------------- SEARCH */

  function initSearch(root) {
    const wrap = root.querySelector('[data-kb-search]');
    if (!wrap || wrap.dataset.kbReady === 'true') return;
    wrap.dataset.kbReady = 'true';

    const toggle = wrap.querySelector('[data-kb-search-toggle]');
    const panel = wrap.querySelector('[data-kb-search-panel]');
    const input = wrap.querySelector('input[type="search"]');
    if (!toggle || !panel || !input) return;

    const syncQuery = () => {
      wrap.classList.toggle('has-query', input.value.trim().length > 0);
    };

    /* Keep the panel inside the screen below the header */
    const fitPanel = () => {
      const header = document.querySelector('.section-header');
      if (!header) return;
      const bottom = header.getBoundingClientRect().bottom;
      panel.style.maxHeight = `${Math.max(240, window.innerHeight - bottom - 20)}px`;
    };

    const isOpen = () => wrap.classList.contains('is-open');

    function open() {
      if (isOpen()) return;
      fitPanel();
      wrap.classList.add('is-open');
      toggle.setAttribute('aria-expanded', 'true');
      /* Focus straight away (inside the tap) so phones open the keyboard */
      input.focus({ preventScroll: true });
      if (input.value) input.select();
      document.addEventListener('pointerdown', onOutside, true);
      document.addEventListener('focusin', onOutside);
      window.addEventListener('resize', fitPanel);
    }

    function close(returnFocus) {
      if (!isOpen()) return;
      wrap.classList.remove('is-open');
      toggle.setAttribute('aria-expanded', 'false');
      const ps = wrap.querySelector('predictive-search');
      if (ps && typeof ps.close === 'function') ps.close();
      if (document.activeElement && wrap.contains(document.activeElement)) document.activeElement.blur();
      if (returnFocus) toggle.focus({ preventScroll: true });
      document.removeEventListener('pointerdown', onOutside, true);
      document.removeEventListener('focusin', onOutside);
      window.removeEventListener('resize', fitPanel);
    }

    function onOutside(event) {
      if (!wrap.contains(event.target)) close(false);
    }

    toggle.addEventListener('click', (event) => {
      event.preventDefault();
      isOpen() ? close(true) : open();
    });

    wrap.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && isOpen()) {
        event.stopPropagation();
        close(true);
      }
    });

    input.addEventListener('input', syncQuery);
    input.form && input.form.addEventListener('reset', () => setTimeout(syncQuery, 0));
    syncQuery();

    /* The header hides when scrolling down: close the panel with it */
    const section = document.querySelector('.section-header');
    if (section && 'MutationObserver' in window) {
      new MutationObserver(() => {
        if (section.classList.contains('shopify-section-header-hidden')) close(false);
      }).observe(section, { attributes: true, attributeFilter: ['class'] });
    }

    wrap.kbOpen = open;
    wrap.kbClose = close;
  }

  function onShortcut(event) {
    const wrap = document.querySelector('[data-kb-search]');
    if (!wrap || !wrap.kbOpen) return;

    const target = event.target;
    const typing =
      target &&
      (target.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName));

    const slash = event.key === '/' && !typing && !event.metaKey && !event.ctrlKey && !event.altKey;
    const cmdK = (event.key === 'k' || event.key === 'K') && (event.metaKey || event.ctrlKey);

    if (slash || cmdK) {
      event.preventDefault();
      wrap.kbOpen();
    }
  }

  /* ------------------------------------------------------- CART */

  const money = (() => {
    const currency = (window.Shopify && Shopify.currency && Shopify.currency.active) || 'GBP';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-GB', { style: 'currency', currency });
    } catch (error) {
      return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' });
    }
  })();

  function readCartState() {
    const state = document.querySelector('#cart-icon-bubble [data-kb-cart-state]');
    if (!state) return null;
    return {
      count: parseInt(state.dataset.count || '0', 10),
      total: parseInt(state.dataset.total || '0', 10),
    };
  }

  function updateTip() {
    const tip = document.querySelector('[data-kb-cart-tip]');
    const link = document.getElementById('cart-icon-bubble');
    const state = readCartState();
    if (!tip || !link || !state) return;

    const free = parseInt(link.dataset.freeThreshold || '0', 10);
    if (!free) return;

    if (state.count === 0) {
      tip.textContent = `Free delivery on orders over ${money.format(free / 100).replace(/\.00$/, '')}`;
    } else if (state.total < free) {
      tip.innerHTML = `<strong>${money.format((free - state.total) / 100)}</strong> away from free delivery`;
    } else {
      tip.innerHTML = '<strong>Free delivery</strong> unlocked';
    }
  }

  let lastCount = null;
  let refreshTimer = null;
  let refreshing = false;

  function bump() {
    const link = document.getElementById('cart-icon-bubble');
    if (!link || reduceMotion.matches) return;
    link.classList.remove('is-bump');
    void link.offsetWidth;
    link.classList.add('is-bump');
    setTimeout(() => link.classList.remove('is-bump'), 700);
  }

  function afterRender() {
    const state = readCartState();
    if (!state) return;
    if (lastCount !== null && state.count > lastCount) bump();
    lastCount = state.count;
    updateTip();
  }

  async function refreshCart() {
    if (refreshing) {
      scheduleRefresh();
      return;
    }
    refreshing = true;
    try {
      const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
      const response = await fetch(`${root}?sections=cart-icon-bubble`, { credentials: 'same-origin' });
      if (!response.ok) return;
      const sections = await response.json();
      const html = sections && sections['cart-icon-bubble'];
      const link = document.getElementById('cart-icon-bubble');
      if (!html || !link) return;
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const inner = doc.querySelector('.shopify-section');
      if (!inner || !inner.querySelector('[data-kb-cart-state]')) return;
      link.innerHTML = inner.innerHTML;
      afterRender();
    } catch (error) {
      /* Network hiccup: the next cart change will try again */
    } finally {
      refreshing = false;
    }
  }

  function scheduleRefresh() {
    clearTimeout(refreshTimer);
    refreshTimer = setTimeout(refreshCart, 350);
  }

  /* Any request to /cart/add, /cart/change, /cart/update or /cart/clear
     (Dawn, the Kitchen Builder app, apps) refreshes the button. */
  function watchCartRequests() {
    if (!('PerformanceObserver' in window)) return;
    const pattern = /\/cart\/(add|change|update|clear)(\.js|\.json)?(\?|$)/;
    try {
      const observer = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          let path = entry.name;
          try {
            path = new URL(entry.name, location.href).pathname;
          } catch (error) {}
          if (pattern.test(path)) {
            scheduleRefresh();
            break;
          }
        }
      });
      observer.observe({ type: 'resource', buffered: false });
    } catch (error) {}

    /* Other scripts that re-render the button themselves */
    const link = document.getElementById('cart-icon-bubble');
    if (link && 'MutationObserver' in window) {
      new MutationObserver(() => {
        if (!refreshing) afterRender();
      }).observe(link, { childList: true });
    }
  }

  /* ------------------------------------------------------- MENU */

  function markCurrent(root) {
    const links = root.querySelectorAll('.kb-hdr a.kb-header-nav__link[href]');
    if (!links.length) return;

    const here = location.pathname.replace(/\/$/, '') || '/';
    const hash = location.hash;
    let best = null;
    let bestScore = 0;

    links.forEach((link) => {
      link.classList.remove('is-current');
      let url;
      try {
        url = new URL(link.getAttribute('href'), location.href);
      } catch (error) {
        return;
      }
      if (url.origin !== location.origin) return;
      const path = url.pathname.replace(/\/$/, '') || '/';
      if (path !== here) return;

      let score = 1;
      if (url.hash) {
        if (url.hash !== hash) return;
        score = 2;
      }
      if (score > bestScore) {
        best = link;
        bestScore = score;
      }
    });

    if (best) best.classList.add('is-current');
  }

  /* ------------------------------------------------------- INIT */

  function init(root = document) {
    initSearch(root);
    markCurrent(root);
    afterRender();
  }

  window.KBHeader = { refreshCart, init };

  document.addEventListener('keydown', onShortcut);
  window.addEventListener('hashchange', () => markCurrent(document));

  const start = () => {
    init(document);
    watchCartRequests();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target || document));
})();
